/**
 * show-discount-pricing-mk.js
 * Version: dp-js-v8-2026-01-14
 *
 * Anpassung:
 * - Kein initiales "Doppelt-Rendern" der Preise auf der PDP.
 * - Solange noch keine Worker-Daten da sind, bleibt der native Theme-Preis sichtbar.
 * - Erst nach erfolgreichem hydrateAllFresh() wird einmalig gerendert.
 * - Danach wie gewohnt Updates bei Variant, Bundle, Subscription usw.
 * - MutationObserver triggert erst nach dem ersten erfolgreichen Render.
 * - boot() wird pro URL nur einmal ausgeführt (DOMContentLoaded, pageshow, popstate feuern sonst mehrfach).
 *
 * Hinweis:
 * - Cache bleibt entfernt.
 * - localStorage wird weiterhin beim Boot komplett geleert (wie in v7).
 */
(function () {
  "use strict";

  const VERSION = "dp-js-v8-2026-01-14";

  const LS_CODE = "hr_discount_code_active";
  const LS_SET_AT = "hr_discount_code_set_at";

  const COOKIE_CODE = "hr_dp_code";
  const COOKIE_SET_AT = "hr_dp_set_at";

  const CODE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

  const SEL_UI = ".hr-price-ui";
  const COLLECTION_BANNER_ID = "hr-dp-collection-banner";

  const KS_PRICE_SEL = '.ks-discounted-price[data-testid="discounted-price"]';
  const KS_ONE_TIME_SEL = '.kaching-subscriptions__card-price[data-testid="one-time-price"]';

  const KS_CARD_INFO_SEL = ".ks-card-info";
  const KS_CARD_INFO_SELECTED_SEL = ".ks-card-info.selected";
  const KS_ONE_TIME_CARD_SELECTED_SEL = ".kaching-subscriptions__card-info.selected";
  const KS_FEATURES_SEL = ".ks-features";

  const KB_BAR_SEL = ".kaching-bundles__bar";
  const KB_BAR_TITLE_SEL = ".kaching-bundles__bar-title";
  const KB_BAR_SUBTITLE_SEL = ".kaching-bundles__bar-subtitle";
  const KB_BAR_PRICE_SEL = ".kaching-bundles__bar-price";
  const KB_BAR_FULL_SEL = ".kaching-bundles__bar-full-price";
  const KB_BAR_UNIT_SEL = ".kaching-bundles__bar-unit-price";

  const THEME_ON_SALE_BADGE_SEL = "on-sale-badge.badge.badge--on-sale";

  // -------------------------
  // storage helper (wie vorher)
  // -------------------------
  const storage = (() => {
    const mem = new Map();
    let ls = null;

    try {
      const k = "__hr_dp_test__";
      window.localStorage.setItem(k, "1");
      window.localStorage.removeItem(k);
      ls = window.localStorage;
    } catch {}

    return {
      get(key) {
        try { if (ls) return ls.getItem(key); } catch {}
        return mem.has(key) ? mem.get(key) : null;
      },
      set(key, val) {
        const s = String(val);
        try {
          if (ls) { ls.setItem(key, s); return; }
        } catch {}
        mem.set(key, s);
      },
      remove(key) {
        try { if (ls) ls.removeItem(key); } catch {}
        mem.delete(key);
      },
      keys() {
        const out = [];
        if (ls) {
          try {
            for (let i = 0; i < ls.length; i++) {
              const k = ls.key(i);
              if (k) out.push(k);
            }
          } catch {}
        } else {
          for (const k of mem.keys()) out.push(k);
        }
        return out;
      },
      clearAll() {
        try { if (ls) ls.clear(); } catch {}
        mem.clear();
      }
    };
  })();

  function setCookie(name, value, maxAgeSeconds) {
    try {
      const v = encodeURIComponent(String(value || ""));
      document.cookie = `${name}=${v}; Max-Age=${Math.max(0, Math.floor(maxAgeSeconds || 0))}; Path=/; SameSite=Lax`;
    } catch {}
  }

  function getCookie(name) {
    try {
      const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const m = document.cookie.match(new RegExp(`(?:^|;\\s*)${escaped}=([^;]*)`));
      return m ? decodeURIComponent(m[1]) : "";
    } catch {
      return "";
    }
  }

  function normalizeCode(code) {
    return String(code || "").trim().toUpperCase();
  }

  function uniq(arr) { return Array.from(new Set(arr)); }

  function formatMoneyFromCents(cents, currency) {
    const n = Number(cents);
    if (!Number.isFinite(n)) return "";
    return (n / 100).toLocaleString("de-DE", { style: "currency", currency: currency || "EUR" });
  }

  function formatUnitLabel(label) {
    if (!label) return "";
    return String(label)
      .replace("1 KG", "kg").replace("KG", "kg")
      .replace("1 G", "g").replace("G", "g")
      .replace("1 L", "l").replace("L", "l")
      .replace("1 ML", "ml").replace("ML", "ml");
  }

  // -------------------------
  // Code persistieren (wie vorher)
  // -------------------------
  function getStoredCode() {
    const codeRaw = (storage.get(LS_CODE) || getCookie(COOKIE_CODE) || "").trim();
    const setAtRaw = storage.get(LS_SET_AT) || getCookie(COOKIE_SET_AT) || "0";
    const setAt = Number(setAtRaw || "0");

    const code = normalizeCode(codeRaw);
    if (!code || !setAt) return "";
    if (Date.now() - setAt > CODE_MAX_AGE_MS) return "";
    return code;
  }

  function setStoredCode(code) {
    const c = normalizeCode(code);
    if (!c) return;

    const now = Date.now();
    storage.set(LS_CODE, c);
    storage.set(LS_SET_AT, String(now));

    setCookie(COOKIE_CODE, c, Math.floor(CODE_MAX_AGE_MS / 1000));
    setCookie(COOKIE_SET_AT, String(now), Math.floor(CODE_MAX_AGE_MS / 1000));
  }

  function clearStoredCode() {
    storage.remove(LS_CODE);
    storage.remove(LS_SET_AT);
    setCookie(COOKIE_CODE, "", 0);
    setCookie(COOKIE_SET_AT, "", 0);
  }

  // -------------------------
  // localStorage beim Laden leeren (wie vorher)
  // -------------------------
  function clearAllLocalStorageOnLoad() {
    try { storage.clearAll(); } catch {}
  }

  window.HRDP = window.HRDP || {};
  window.HRDP.clearCache = clearAllLocalStorageOnLoad;
  window.HRDP.version = VERSION;

  // -------------------------
  // cart fetch (wie vorher)
  // -------------------------
  async function fetchCart() {
    const res = await fetch("/cart.js", { credentials: "same-origin", cache: "no-store" });
    if (!res.ok) return null;
    return res.json();
  }

  let cartCache = null;
  let cartCacheAt = 0;
  let cartInFlight = null;

  async function fetchCartCached() {
    const now = Date.now();
    if (cartCache && (now - cartCacheAt) < 1500) return cartCache;
    if (cartInFlight) return cartInFlight;

    cartInFlight = fetchCart().finally(() => { cartInFlight = null; });
    const cart = await cartInFlight;
    if (cart) {
      cartCache = cart;
      cartCacheAt = now;
    }
    return cart;
  }

  function getApplicableCodeFromCart(cart) {
    const list = Array.isArray(cart?.discount_codes) ? cart.discount_codes : [];
    const found = list.find(d => d && d.applicable && d.code);
    return found ? normalizeCode(found.code) : "";
  }

  async function fetchPricing(code, ids) {
    const qs = new URLSearchParams();
    qs.set("code", code);
    qs.set("variants", ids.join(","));
    qs.set("qty", "1");

    const res = await fetch("/apps/discount-pricing?" + qs.toString(), {
      credentials: "same-origin",
      cache: "no-store"
    });

    if (!res.ok) return null;
    return res.json().catch(() => null);
  }

  // -------------------------
  // UI helpers (wie vorher)
  // -------------------------
  function findPriceListNear(ui) {
    if (!ui) return null;

    const inside = ui.closest && ui.closest("price-list");
    if (inside) return inside;

    const wrap = ui.closest && ui.closest(".hr-price-wrap");
    if (wrap) {
      const pl = wrap.querySelector("price-list");
      if (pl) return pl;
    }

    const parent = ui.parentElement;
    if (parent) {
      const pl = parent.querySelector("price-list");
      if (pl) return pl;
    }

    const container =
      (ui.closest && ui.closest("product-card, .product-card, li, .product, product-rerender")) ||
      null;

    if (container) {
      const pl = container.querySelector("price-list");
      if (pl) return pl;
    }

    return null;
  }

  function hideNative(ui) {
    const pl = findPriceListNear(ui);
    if (pl) pl.style.display = "none";
  }

  function showNative(ui) {
    const pl = findPriceListNear(ui);
    if (pl) pl.style.display = "";
  }

  function detectHasNativeUnitPrice(ui) {
    if (!ui) return false;

    if (ui.dataset.hrHasNativeUnit === "1") return true;
    if (ui.dataset.hrHasNativeUnit === "0") return false;

    const pl = findPriceListNear(ui);
    const unit = pl ? pl.querySelector("unit-price") : null;
    const has = !!(unit && (unit.textContent || "").trim().length > 0);

    ui.dataset.hrHasNativeUnit = has ? "1" : "0";
    return has;
  }

  function hideThemeBadgesAround(ui) {
    const wrap =
      (ui.closest && ui.closest(".hr-price-wrap")) ||
      ui.parentElement ||
      (ui.closest && ui.closest("li")) ||
      (ui.closest && ui.closest(".product-card")) ||
      (ui.closest && ui.closest("product-card")) ||
      null;

    if (!wrap) return;
    wrap.querySelectorAll("on-sale-badge").forEach(b => { b.style.display = "none"; });
  }

  function showThemeBadgesAround(ui) {
    const wrap =
      (ui.closest && ui.closest(".hr-price-wrap")) ||
      ui.parentElement ||
      (ui.closest && ui.closest("li")) ||
      (ui.closest && ui.closest(".product-card")) ||
      (ui.closest && ui.closest("product-card")) ||
      null;

    if (!wrap) return;
    wrap.querySelectorAll("on-sale-badge").forEach(b => { b.style.display = ""; });
  }

  function hideGlobalThemeBadges() {
    document.querySelectorAll(THEME_ON_SALE_BADGE_SEL).forEach(el => {
      if (el.dataset.hrDpOrigDisplay == null) el.dataset.hrDpOrigDisplay = el.style.display || "";
      el.style.display = "none";
    });
  }

  function showGlobalThemeBadges() {
    document.querySelectorAll(THEME_ON_SALE_BADGE_SEL).forEach(el => {
      if (el.dataset.hrDpOrigDisplay != null) el.style.display = el.dataset.hrDpOrigDisplay || "";
      else el.style.display = "";
    });
  }

  function clearUI(ui) {
    ui.innerHTML = "";
    ui.removeAttribute("data-active");
    ui.removeAttribute("data-stamp");
    showNative(ui);
    showThemeBadgesAround(ui);
  }

  function isPdpUi(ui) {
    return String(ui?.getAttribute("data-scope") || "") === "pdp";
  }

  function isCardUi(ui) {
    return String(ui?.getAttribute("data-scope") || "") === "card";
  }

  function isSubscriptionCardSelected() {
    return !!document.querySelector(KS_CARD_INFO_SELECTED_SEL);
  }

  function isOneTimeCardSelected() {
    return !!document.querySelector(KS_ONE_TIME_CARD_SELECTED_SEL);
  }

  function getSubscriptionFactorForPdp() {
    if (isOneTimeCardSelected()) return 1.0;
    if (isSubscriptionCardSelected()) return 0.9;
    return 1.0;
  }

  function computeEffectivePercent(basePercent, factor) {
    const p = Number(basePercent) || 0;
    const f = Number(factor) || 1;
    const effective = ((1 - (p / 100)) * f) * 100 - 100;
    return Math.round(effective);
  }

  // -------------------------
  // Statt Cache: In-Memory Ergebnisse pro Run
  // -------------------------
  let latestPricing = { cart: null, variants: {} };

  function getVariantData(variantId) {
    return latestPricing && latestPricing.variants ? latestPricing.variants[variantId] : null;
  }

  function renderUI(ui, data, codeActive) {
    if (!ui) return;

    const hasNativeUnit = detectHasNativeUnitPrice(ui);

    if (!codeActive) {
      clearUI(ui);
      return;
    }

    if (!data || !data.applied) {
      ui.innerHTML = "";
      ui.removeAttribute("data-active");
      ui.removeAttribute("data-stamp");
      showNative(ui);
      hideThemeBadgesAround(ui);
      return;
    }

    hideNative(ui);
    hideThemeBadgesAround(ui);
    ui.setAttribute("data-active", "1");

    const subsFactor = (isPdpUi(ui) ? getSubscriptionFactorForPdp() : 1.0);

    const displayStrikeCents = Number(data.strike_cents);
    const displayPriceCents = Math.round(Number(data.price_cents) * subsFactor);

    const workerUnitFlag = (typeof data.unitApplicable === "boolean") ? data.unitApplicable : null;
    const shouldShowUnit =
      (workerUnitFlag !== null ? workerUnitFlag : hasNativeUnit) &&
      data.unitPrice_cents != null &&
      data.unitLabel;

    const displayUnitCents = shouldShowUnit ? Math.round(Number(data.unitPrice_cents) * subsFactor) : null;

    const displayPercent = (isPdpUi(ui) && subsFactor !== 1.0)
      ? computeEffectivePercent(data.percent, subsFactor)
      : (-(Number(data.percent) || 0));

    const stamp = [
      displayStrikeCents,
      displayPriceCents,
      displayPercent,
      (shouldShowUnit ? displayUnitCents : ""),
      (shouldShowUnit ? (data.unitLabel || "") : "")
    ].join("|");

    if (ui.getAttribute("data-stamp") === stamp) return;
    ui.setAttribute("data-stamp", stamp);

    const strike = formatMoneyFromCents(displayStrikeCents, data.currency);
    const price = formatMoneyFromCents(displayPriceCents, data.currency);

    const unitLine = (shouldShowUnit && Number(displayUnitCents) > 0)
      ? `<div class="text-subdued text-sm unit-price">
           (${formatMoneyFromCents(displayUnitCents, data.currency)}/${formatUnitLabel(data.unitLabel)})
         </div>`
      : "";

    if (isCardUi(ui)) {
      ui.innerHTML = `
        <div class="v-stack gap-1">
          <div class="text-subdued line-through">${strike}</div>
          <div class="h-stack gap-3 items-center">
            <span class="price price--highlight">${price}</span>
            <on-sale-badge-dp class="badge badge--on-sale">${displayPercent}%</on-sale-badge-dp>
          </div>
          ${unitLine}
        </div>
      `;
      return;
    }

    ui.innerHTML = `
      <div class="h-stack gap-3 items-center">
        <span class="text-subdued line-through">${strike}</span>
        <span class="price price--highlight">${price}</span>
        <on-sale-badge-dp class="badge badge--on-sale">${displayPercent}%</on-sale-badge-dp>
      </div>
      ${unitLine}
    `;
  }

  function syncVariantFromFormIfNeeded(ui) {
    const scope = String(ui.getAttribute("data-scope") || "");
    if (scope === "card") return;

    const wrap = (ui.closest && ui.closest("product-rerender, .product, .quick-buy-drawer, .drawer")) || document;
    let form = wrap.querySelector('form[is="product-form"], form.shopify-product-form, product-form form');
    if (!form) form = (ui.closest && ui.closest("form")) || null;

    const idInput = form ? form.querySelector('input[name="id"]') : wrap.querySelector('input[name="id"]');
    const val = idInput ? String(idInput.value || "").trim() : "";
    if (val) ui.setAttribute("data-variant-id", val);
  }

  function collectVisibleVariantIds() {
    const uis = Array.from(document.querySelectorAll(SEL_UI));
    uis.forEach(ui => {
      syncVariantFromFormIfNeeded(ui);
      detectHasNativeUnitPrice(ui);
    });
    return uniq(
      uis.map(el => String(el.getAttribute("data-variant-id") || "").trim())
        .filter(v => v && v !== "0")
    );
  }

  function collectQuickBuyVariantIds() {
    const ids = [];
    const roots = Array.from(document.querySelectorAll("product-rerender, .quick-buy-drawer, .drawer"));
    roots.forEach(root => {
      const scripts = root.querySelectorAll('variant-picker script[type="application/json"]');
      scripts.forEach(s => {
        const txt = (s.textContent || "").trim();
        if (!txt) return;
        try {
          const obj = JSON.parse(txt);
          if (obj && obj.id) ids.push(String(obj.id));
        } catch {}
      });
    });
    return uniq(ids.filter(Boolean));
  }

  async function hydrateAllFresh(code, wantedIds) {
    const out = { cart: null, variants: {} };
    if (!wantedIds.length) return { ok: true, data: out };

    const batchSize = 80;
    for (let i = 0; i < wantedIds.length; i += batchSize) {
      const chunk = wantedIds.slice(i, i + batchSize);
      const payload = await fetchPricing(code, chunk);

      if (!payload) return { ok: false, transient: true, data: out };
      if (payload.codeApplicable === false) return { ok: false, invalid: true, data: out };
      if (!payload.variants || typeof payload.variants !== "object") return { ok: false, transient: true, data: out };

      if (payload.cart && typeof payload.cart === "object") out.cart = payload.cart;
      Object.assign(out.variants, payload.variants);
    }

    return { ok: true, data: out };
  }

  function renderAllFromLatest(codeActive) {
    const uis = Array.from(document.querySelectorAll(SEL_UI));
    uis.forEach(ui => {
      syncVariantFromFormIfNeeded(ui);
      detectHasNativeUnitPrice(ui);
      const vid = String(ui.getAttribute("data-variant-id") || "").trim();
      renderUI(ui, getVariantData(vid), codeActive);
    });
  }

  function getPdpVariantId() {
    const pdp = document.querySelector('.hr-price-ui[data-scope="pdp"]');
    if (!pdp) return "";
    syncVariantFromFormIfNeeded(pdp);
    return String(pdp.getAttribute("data-variant-id") || "").trim();
  }

  function getSelectedBundleQty() {
    const selected =
      document.querySelector(`${KB_BAR_SEL}.kaching-bundles__bar--selected`) ||
      (document.querySelector(`${KB_BAR_SEL} input[type="radio"]:checked`)?.closest(KB_BAR_SEL)) ||
      null;

    if (!selected) return 1;

    const title = (selected.querySelector(KB_BAR_TITLE_SEL)?.textContent || "").trim();
    const m = title.match(/(\d+)/);
    const n = m ? parseInt(m[1], 10) : NaN;
    return (Number.isFinite(n) && n > 0) ? n : 1;
  }

  function getBundleBarFactor(qty) {
    if (qty === 2) return 0.95;
    if (qty >= 3) return 0.9;
    return 1.0;
  }

  function ensureKachingFeatureItem(codeActive, appliedForPdp) {
    const features = document.querySelector(KS_FEATURES_SEL);
    if (!features) return;

    const marker = features.querySelector('[data-hr-dp-feature="1"]');

    if (!(codeActive && appliedForPdp)) {
      if (marker) marker.remove();
      return;
    }

    if (marker) return;

    const html = `
      <div class="ks-feature-item" data-hr-dp-feature="1">
        <div class="kaching-subscriptions__flex-col-center">
          <div class="kaching-subscriptions__check-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M12.0303 5.71967C12.3232 6.01256 12.3232 6.48744 12.0303 6.78033L7.53033 11.2803C7.23744 11.5732 6.76256 11.5732 6.46967 11.2803L4.21967 9.03033C3.92678 8.73744 3.92678 8.26256 4.21967 7.96967C4.51256 7.67678 4.98744 7.67678 5.28033 7.96967L7 9.68934L10.9697 5.71967C11.2626 5.42678 11.7374 5.42678 12.0303 5.71967Z" fill="white"></path>
            </svg>
          </div>
        </div>
        <div class="kaching-subscriptions__flex-col-center">
          <span class="ks-feature-item-text">Aktionsrabatt für die 1. Bestellung angewendet</span>
        </div>
      </div>
    `;

    features.insertAdjacentHTML("beforeend", html);
  }

  function updateKachingBundlesBars(codeActive) {
    const bars = Array.from(document.querySelectorAll(KB_BAR_SEL));
    if (!bars.length) return;

    if (!codeActive) {
      bars.forEach(bar => {
        [KB_BAR_SUBTITLE_SEL, KB_BAR_PRICE_SEL, KB_BAR_FULL_SEL, KB_BAR_UNIT_SEL].forEach(sel => {
          const el = bar.querySelector(sel);
          if (el && el.dataset.hrDpOrig != null) el.textContent = el.dataset.hrDpOrig;
        });
      });
      return;
    }

    const vid = getPdpVariantId();
    if (!vid) return;

    const data = getVariantData(vid);

    if (!data || !data.applied) {
      bars.forEach(bar => {
        [KB_BAR_SUBTITLE_SEL, KB_BAR_PRICE_SEL, KB_BAR_FULL_SEL, KB_BAR_UNIT_SEL].forEach(sel => {
          const el = bar.querySelector(sel);
          if (el && el.dataset.hrDpOrig != null) el.textContent = el.dataset.hrDpOrig;
        });
      });
      return;
    }

    const pdpSubFactor = isSubscriptionCardSelected() ? 0.9 : 1.0;
    const subtitlePercent = (pdpSubFactor !== 1.0)
      ? Math.abs(computeEffectivePercent(data.percent, pdpSubFactor))
      : (Number(data.percent) || 0);

    const unitText = (data.unitApplicable && data.unitPrice_cents && data.unitLabel)
      ? `${formatMoneyFromCents(Math.round(Number(data.unitPrice_cents) * pdpSubFactor), data.currency)} / ${formatUnitLabel(data.unitLabel)}`
      : null;

    bars.forEach(bar => {
      const title = (bar.querySelector(KB_BAR_TITLE_SEL)?.textContent || "").trim();
      const m = title.match(/(\d+)/);
      const mult = (m && Number.isFinite(parseInt(m[1], 10))) ? parseInt(m[1], 10) : 1;

      const subtitleEl = bar.querySelector(KB_BAR_SUBTITLE_SEL);
      const priceEl = bar.querySelector(KB_BAR_PRICE_SEL);
      const fullEl = bar.querySelector(KB_BAR_FULL_SEL);
      const unitEl = bar.querySelector(KB_BAR_UNIT_SEL);

      if (subtitleEl) {
        if (subtitleEl.dataset.hrDpOrig == null) subtitleEl.dataset.hrDpOrig = subtitleEl.textContent || "";
        subtitleEl.textContent = `${subtitlePercent}% Rabatt`;
      }

      if (priceEl) {
        if (priceEl.dataset.hrDpOrig == null) priceEl.dataset.hrDpOrig = priceEl.textContent || "";
        const cents = Math.round(Number(data.price_cents) * pdpSubFactor) * mult;
        priceEl.textContent = formatMoneyFromCents(cents, data.currency);
      }

      if (fullEl) {
        if (fullEl.dataset.hrDpOrig == null) fullEl.dataset.hrDpOrig = fullEl.textContent || "";
        const barFactor = getBundleBarFactor(mult);
        const cents = Math.round(Number(data.strike_cents) * mult * barFactor);
        fullEl.textContent = formatMoneyFromCents(cents, data.currency);
      }

      if (unitEl) {
        if (unitEl.dataset.hrDpOrig == null) unitEl.dataset.hrDpOrig = unitEl.textContent || "";
        unitEl.textContent = unitText ? unitText : unitEl.dataset.hrDpOrig;
      }
    });
  }

  function updateKachingOneTimePrice(codeActive) {
    const el = document.querySelector(KS_ONE_TIME_SEL);
    if (!el) return;

    if (!codeActive) {
      if (el.dataset.hrDpOrig != null) el.textContent = el.dataset.hrDpOrig;
      return;
    }

    const vid = getPdpVariantId();
    if (!vid) return;

    const data = getVariantData(vid);

    if (el.dataset.hrDpOrig == null) el.dataset.hrDpOrig = el.textContent || "";

    if (!data || !data.applied) {
      el.textContent = el.dataset.hrDpOrig;
      return;
    }

    const mult = getSelectedBundleQty();
    el.textContent = formatMoneyFromCents(Number(data.price_cents) * mult, data.currency);
  }

  function updateKsDiscountedPrice(codeActive) {
    const el = document.querySelector(KS_PRICE_SEL);
    if (!el) return;

    if (!codeActive) {
      if (el.dataset.hrDpOrig != null) el.innerHTML = el.dataset.hrDpOrig;
      return;
    }

    const vid = getPdpVariantId();
    if (!vid) return;

    const data = getVariantData(vid);

    if (!data || !data.applied) {
      if (el.dataset.hrDpOrig != null) el.innerHTML = el.dataset.hrDpOrig;
      return;
    }

    if (el.dataset.hrDpOrig == null) el.dataset.hrDpOrig = el.innerHTML;

    const mult = getSelectedBundleQty();
    const barFactor = getBundleBarFactor(mult);

    const discountedCents = Math.round(Number(data.price_cents) * 0.9) * mult;
    const regularCents = Math.round(Number(data.strike_cents) * mult * barFactor);

    const discounted = formatMoneyFromCents(discountedCents, data.currency);
    const regular = formatMoneyFromCents(regularCents, data.currency);

    el.innerHTML = `${discounted} <span style="opacity:.7">(ab der 2. Lieferung, regulär ${regular})</span>`;
  }

  function updateAllKaching(codeActive) {
    const vid = getPdpVariantId();
    const data = vid ? getVariantData(vid) : null;
    const appliedForPdp = !!(data && data.applied);

    updateKachingBundlesBars(codeActive);
    updateKachingOneTimePrice(codeActive);
    updateKsDiscountedPrice(codeActive);
    ensureKachingFeatureItem(codeActive, appliedForPdp);
  }

  function isCollectionPage() {
    const p = String(location.pathname || "");
    if (p.startsWith("/collections/")) return true;

    if (document.querySelector("[data-section-type='collection-template'], [data-section-id*='collection']")) return true;
    if (document.querySelector(".collection, .collection__content, .collection__products, .collection-products")) return true;

    return false;
  }

  function buildCollectionBannerHtml(code) {
    const safeCode = normalizeCode(code);
    return `
      <div id="${COLLECTION_BANNER_ID}" role="status" aria-live="polite"
        style="background:rgba(246, 245, 241, 1);border-radius:0.375rem;padding:10px 20px;display:flex;gap:14px;align-items:center;margin:18px 0px;margin-top:-10px;">
        <div style="width:34px;height:34px;border-radius:999px;border:2px solid rgba(0,0,0,0.12);display:flex;align-items:center;justify-content:center;flex:0 0 auto;">
          <svg role="presentation" fill="none" focusable="false" stroke-width="1.5" width="18" height="18" class="icon icon-discount" viewBox="0 0 14 13">
            <path d="M7.25 11.383a1.74 1.74 0 0 0 2.195.105c.478-.35.957-.722 1.39-1.155.433-.433.804-.912 1.154-1.39.486-.662.45-1.588-.104-2.195a65.945 65.945 0 0 0-4.97-4.896 1.588 1.588 0 0 0-.75-.377c-1.017-.197-3.552-.613-4.177.012-.625.624-.208 3.16-.011 4.176.055.28.186.537.376.75a65.976 65.976 0 0 0 4.896 4.97Z" fill="none" stroke="currentColor"></path>
            <path d="M5.729 5.227a1.036 1.036 0 1 0-1.466-1.466 1.036 1.036 0 0 0 1.466 1.466Z" fill="currentColor"></path>
          </svg>
        </div>
        <div data-text="1" style="font-size:16px;font-weight:normal;color:rgb(17,17,17);">
          Code „${safeCode}“ aktiviert – du erhältst den Aktionsrabatt auf diese Bestellung!
        </div>
      </div>
    `;
  }

  function findCollectionInsertTarget() {
    const grid =
      document.querySelector(".product-grid, .collection__products, .collection-products, .product-list, .grid") ||
      document.querySelector("[data-section-type*='collection'], [data-section-id*='collection']") ||
      null;

    if (grid && grid.parentElement) return grid;

    const main = document.querySelector("main, #MainContent, [role='main']");
    if (main) return main;

    return document.body;
  }

  function mountCollectionBanner(codeActive, code, anyApplied) {
    if (!isCollectionPage()) return;

    const existing = document.getElementById(COLLECTION_BANNER_ID);

    if (!(codeActive && code && anyApplied)) {
      if (existing) existing.remove();
      return;
    }

    if (existing) {
      const t = existing.querySelector('[data-text="1"]');
      if (t) t.textContent = `Code „${normalizeCode(code)}“ aktiviert – du erhältst den Aktionsrabatt auf diese Bestellung!`;
      return;
    }

    const target = findCollectionInsertTarget();
    if (!target) return;

    const html = buildCollectionBannerHtml(code);

    if (target.matches && target.matches(".product-grid, .collection__products, .collection-products, .product-list, .grid")) {
      target.insertAdjacentHTML("beforebegin", html);
    } else {
      target.insertAdjacentHTML("afterbegin", html);
    }
  }

  function computeAnyAppliedInVisible(wantedIds) {
    for (const id of wantedIds) {
      const v = getVariantData(id);
      if (v && v.applied) return true;
    }
    return false;
  }

  function getCodeFromDiscountReferrer() {
    try {
      if (!document.referrer) return "";
      const u = new URL(document.referrer);
      if (!u.pathname.startsWith("/discount/")) return "";
      const parts = u.pathname.split("/").filter(Boolean);
      if (parts.length < 2) return "";
      return normalizeCode(decodeURIComponent(parts[1] || ""));
    } catch {
      return "";
    }
  }

  function getCodeFromAnyDiscountCookie() {
    const candidates = ["discount_code", "discountCode", "shopify_discount_code", "discount"];
    for (const name of candidates) {
      const v = (getCookie(name) || "").trim();
      if (v) return normalizeCode(v.split(",")[0]);
    }

    try {
      const all = String(document.cookie || "").split(";").map(s => s.trim()).filter(Boolean);
      for (const pair of all) {
        const idx = pair.indexOf("=");
        if (idx <= 0) continue;
        const k = pair.slice(0, idx).trim().toLowerCase();
        const v = decodeURIComponent(pair.slice(idx + 1));
        if (k.includes("discount") && v) return normalizeCode(v.split(",")[0]);
      }
    } catch {}

    return "";
  }

  function applyDiscountRedirectFromQuery() {
    const url = new URL(window.location.href);
    const code = normalizeCode(url.searchParams.get("discount") || "");
    if (!code) return false;

    setStoredCode(code);

    url.searchParams.delete("discount");
    const cleanUrl = url.pathname + (url.searchParams.toString() ? ("?" + url.searchParams.toString()) : "") + url.hash;

    const target = "/discount/" + encodeURIComponent(code) + "?redirect=" + encodeURIComponent(cleanUrl);
    window.location.replace(target);
    return true;
  }

  function captureCodeAfterDiscountRedirectIfNeeded() {
    const already = getStoredCode();
    if (already) return;

    const fromRef = getCodeFromDiscountReferrer();
    if (fromRef) { setStoredCode(fromRef); return; }

    const fromCookie = getCodeFromAnyDiscountCookie();
    if (fromCookie) { setStoredCode(fromCookie); return; }
  }

  // -------------------------
  // Steuerung: Initial nur einmal rendern, erst wenn Daten da sind
  // -------------------------
  let didInitialRender = false;
  let observerEnabled = false;
  let lastBootUrl = "";

  let running = false;
  let pending = false;
  let inFlightFetch = null;

  let scheduled = false;
  function scheduleRun() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      run().catch(() => {});
    });
  }

  async function run() {
    if (running) { pending = true; return; }
    running = true;

    try {
      const uis = Array.from(document.querySelectorAll(SEL_UI));
      uis.forEach(detectHasNativeUnitPrice);

      const cart = await fetchCartCached();

      const storedCode = getStoredCode();
      const cartCode = cart ? getApplicableCodeFromCart(cart) : "";

      let code = "";
      let codeActive = false;

      if (cart && cartCode) {
        code = cartCode;
        codeActive = true;
        setStoredCode(cartCode);
      } else if (cart && (cart.item_count || 0) === 0) {
        code = storedCode;
        codeActive = !!code;
      } else if (!cart) {
        code = storedCode;
        codeActive = !!code;
      } else {
        codeActive = false;
      }

      if (!codeActive) {
        inFlightFetch = null;
        latestPricing = { cart: null, variants: {} };

        didInitialRender = false;
        observerEnabled = false;

        uis.forEach(ui => renderUI(ui, null, false));
        updateAllKaching(false);
        mountCollectionBanner(false, "", false);
        showGlobalThemeBadges();
        return;
      }

      hideGlobalThemeBadges();

      const wanted = uniq(collectVisibleVariantIds().concat(collectQuickBuyVariantIds()));

      if (!wanted.length) {
        latestPricing = { cart: null, variants: {} };

        if (didInitialRender) {
          renderAllFromLatest(true);
          updateAllKaching(true);
        }

        mountCollectionBanner(true, code, false);
        return;
      }

      if (!inFlightFetch) {
        inFlightFetch = hydrateAllFresh(code, wanted).finally(() => { inFlightFetch = null; });
      }

      const hydrated = await inFlightFetch;

      if (hydrated && hydrated.invalid) {
        clearStoredCode();
        latestPricing = { cart: null, variants: {} };

        didInitialRender = false;
        observerEnabled = false;

        uis.forEach(ui => renderUI(ui, null, false));
        updateAllKaching(false);
        mountCollectionBanner(false, "", false);
        showGlobalThemeBadges();
        return;
      }

      if (hydrated && hydrated.ok) {
        latestPricing = hydrated.data || { cart: null, variants: {} };
      } else {
        latestPricing = { cart: null, variants: {} };
      }

      if (!didInitialRender) {
        didInitialRender = true;
        observerEnabled = true;
      }

      renderAllFromLatest(true);
      updateAllKaching(true);

      const anyApplied = computeAnyAppliedInVisible(wanted);
      mountCollectionBanner(true, code, anyApplied);

    } finally {
      running = false;
      if (pending) { pending = false; scheduleRun(); }
    }
  }

  function boot() {
    const currentUrl = String(location.href || "");
    if (lastBootUrl === currentUrl) return;
    lastBootUrl = currentUrl;

    clearAllLocalStorageOnLoad();

    const didRedirect = applyDiscountRedirectFromQuery();
    if (didRedirect) return;

    captureCodeAfterDiscountRedirectIfNeeded();

    didInitialRender = false;
    observerEnabled = false;

    scheduleRun();
  }

  document.addEventListener("change", e => {
    if (e.target.closest("variant-picker")) scheduleRun();
    if (e.target.closest(`${KB_BAR_SEL} input[type="radio"]`)) scheduleRun();
    if (e.target.closest(KS_CARD_INFO_SEL)) scheduleRun();
    if (e.target.closest(".kaching-subscriptions__card-info")) scheduleRun();
    if (e.target.closest('input[name="quantity"], .quantity-selector input, quantity-input input')) scheduleRun();
  });

  document.addEventListener("click", e => {
    if (e.target.closest(KB_BAR_SEL)) scheduleRun();
    if (e.target.closest(KS_CARD_INFO_SEL)) scheduleRun();
    if (e.target.closest(".kaching-subscriptions__card-info")) scheduleRun();
  });

  const mo = new MutationObserver(muts => {
    if (!observerEnabled) return;

    for (const m of muts) {
      if (m.type === "attributes" && m.target && m.target.matches && m.target.matches('input[name="id"]')) {
        scheduleRun();
        return;
      }
      if (m.addedNodes) {
        for (const n of m.addedNodes) {
          if (!(n instanceof Element)) continue;
          if (
            n.matches?.(SEL_UI) || n.querySelector?.(SEL_UI) ||
            n.matches?.("product-rerender") || n.querySelector?.("product-rerender") ||
            n.matches?.("variant-picker") || n.querySelector?.("variant-picker") ||
            n.matches?.('input[name="id"]') || n.querySelector?.('input[name="id"]') ||
            n.matches?.(KS_PRICE_SEL) || n.querySelector?.(KS_PRICE_SEL) ||
            n.matches?.(KS_ONE_TIME_SEL) || n.querySelector?.(KS_ONE_TIME_SEL) ||
            n.matches?.(KS_FEATURES_SEL) || n.querySelector?.(KS_FEATURES_SEL) ||
            n.matches?.(KB_BAR_SEL) || n.querySelector?.(KB_BAR_SEL) ||
            n.matches?.(KS_CARD_INFO_SEL) || n.querySelector?.(KS_CARD_INFO_SEL) ||
            n.matches?.(".kaching-subscriptions__card-info") || n.querySelector?.(".kaching-subscriptions__card-info") ||
            n.matches?.(THEME_ON_SALE_BADGE_SEL) || n.querySelector?.(THEME_ON_SALE_BADGE_SEL)
          ) {
            scheduleRun();
            return;
          }
        }
      }
    }
  });
  mo.observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ["value", "class", "style"] });

  document.addEventListener("DOMContentLoaded", boot);
  window.addEventListener("pageshow", boot);
  window.addEventListener("popstate", boot);
})();